/*
 **	SFC Calibration (c) 2026 Silver Falls Capital. All Rights Reserved
 **/

import { useEffect, useState } from 'react';
import { RefreshCw, X } from 'lucide-react';
import { theme } from '../../theme';
import { CURRENT_BUILD_ID, fetchServedBuildId, isNewBuildAvailable } from '../../utils/buildVersion';

const POLL_MS = 5 * 60 * 1000;

/**
 * Shows a fixed "new version available" bar once /version.json reports a build
 * other than the one running. Checks every five minutes and whenever the tab
 * regains focus. Dismissible, because an operator mid-calibration should
 * finish and then reload; the bar comes back on the next check.
 */
export function UpdateBanner() {
	const [available, setAvailable] = useState(false);
	const [dismissedFor, setDismissedFor] = useState<string | null>(null);
	const [servedId, setServedId] = useState<string | null>(null);

	useEffect(() => {
		let cancelled = false;
		const controller = new AbortController();

		const check = async () => {
			const served = await fetchServedBuildId(controller.signal);
			if (cancelled || served === null) return;
			setServedId(served);
			setAvailable(isNewBuildAvailable(CURRENT_BUILD_ID, served));
		};

		void check();
		const timer = window.setInterval(check, POLL_MS);
		const onVisible = () => {
			if (document.visibilityState === 'visible') void check();
		};
		document.addEventListener('visibilitychange', onVisible);
		window.addEventListener('focus', onVisible);
		return () => {
			cancelled = true;
			controller.abort();
			window.clearInterval(timer);
			document.removeEventListener('visibilitychange', onVisible);
			window.removeEventListener('focus', onVisible);
		};
	}, []);

	if (!available || dismissedFor === servedId) return null;

	return (
		<div
			role="status"
			style={{
				position: 'fixed',
				left: 0,
				right: 0,
				bottom: 0,
				zIndex: 1000,
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				gap: '16px',
				padding: '12px 20px',
				background: theme.colors.text.primary,
				color: 'white',
				boxShadow: '0 -2px 8px rgba(0,0,0,0.2)',
				...theme.typography.body,
			}}
		>
			<span>A new version of the calibration app is available. Finish the current step, then reload.</span>
			<button
				onClick={() => window.location.reload()}
				style={{
					display: 'inline-flex',
					alignItems: 'center',
					gap: '6px',
					padding: '6px 14px',
					border: 'none',
					borderRadius: '4px',
					background: theme.colors.primary,
					color: 'white',
					fontWeight: 600,
					cursor: 'pointer',
				}}
			>
				<RefreshCw size={14} /> Reload now
			</button>
			<button
				aria-label="Dismiss until the next check"
				onClick={() => setDismissedFor(servedId)}
				style={{ background: 'transparent', border: 'none', color: 'white', cursor: 'pointer', display: 'inline-flex' }}
			>
				<X size={16} />
			</button>
		</div>
	);
}
